import { Routes, Route, Navigate } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { apiScopes } from "./auth/msalConfig";
import { api } from "./api/client";
import LandingPage from "./pages/LandingPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ApprovalDetailPage from "./pages/ApprovalDetailPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import AppShell from "./components/AppShell";
import AdminLanding from "./pages/admin/AdminLanding";
import CategoriesAdmin from "./pages/admin/CategoriesAdmin";
import PolicyAdmin from "./pages/admin/PolicyAdmin";
import MileageRatesAdmin from "./pages/admin/MileageRatesAdmin";
import EmailPreviewsAdmin from "./pages/admin/EmailPreviewsAdmin";
import CostCentresAdmin from "./pages/admin/CostCentresAdmin";
import ManagerOverridesAdmin from "./pages/admin/ManagerOverridesAdmin";
import RolesAdmin from "./pages/admin/RolesAdmin";
import AllClaimsAdmin from "./pages/admin/AllClaimsAdmin";
import EmailPrefsAdmin from "./pages/admin/EmailPrefsAdmin";
import NotificationsAdmin from "./pages/admin/NotificationsAdmin";
import ClaimantProfile from "./pages/admin/ClaimantProfile";
import AutoActionPage from "./pages/AutoActionPage";
import FinanceLanding from "./pages/finance/FinanceLanding";
import ReportsPage from "./pages/finance/ReportsPage";
import ComingSoon from "./pages/ComingSoon";

function RequireAuth({ children }: { children: (roles: string[]) => React.ReactNode }) {
  const isAuthed = useIsAuthenticated();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me, enabled: isAuthed });
  if (!isAuthed) return <Navigate to="/login" replace />;
  return <>{children(me?.roles ?? [])}</>;
}

function SmartRedirect({ roles }: { roles: string[] }) {
  const adminRoles = ["Admin", "Payroll", "Finance", "HomeManager"];
  if (roles.some((r) => adminRoles.includes(r))) return <Navigate to="/admin" replace />;
  if (roles.includes("Approver")) return <Navigate to="/approvals" replace />;
  return <LandingPage roles={roles} />;
}

export default function App() {
  const { instance } = useMsal();
  const isAuthed = useIsAuthenticated();

  if (isAuthed && instance.getAllAccounts().length > 0) {
    instance
      .acquireTokenSilent({ scopes: apiScopes, account: instance.getAllAccounts()[0] })
      .catch(() => {});
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth-callback" element={<AuthCallback />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            {(roles) => (
              <AppShell roles={roles}>
                <Routes>
                  <Route path="/" element={<SmartRedirect roles={roles} />} />
                  <Route path="/approvals" element={<ApprovalsPage defaultStage="manager" />} />
                  <Route path="/approvals/:id" element={<ApprovalDetailPage />} />
                  <Route path="/auto" element={<AutoActionPage />} />

                  {/* All admin sections live under /admin or as routed siblings */}
                  <Route path="/admin" element={<AdminLanding />} />
                  <Route path="/admin/categories" element={<CategoriesAdmin />} />
                  <Route path="/admin/mileage-rates" element={<MileageRatesAdmin />} />
                  <Route path="/admin/cost-centres" element={<CostCentresAdmin />} />
                  <Route path="/admin/policy" element={<PolicyAdmin />} />
                  <Route path="/admin/email-previews" element={<EmailPreviewsAdmin />} />
                  <Route path="/admin/manager-overrides" element={<ManagerOverridesAdmin />} />
                  <Route path="/admin/roles" element={<RolesAdmin />} />
                  <Route path="/admin/all-claims" element={<AllClaimsAdmin />} />
                  <Route path="/admin/email-prefs" element={<EmailPrefsAdmin />} />
                  <Route path="/admin/notifications" element={<NotificationsAdmin />} />
                  <Route path="/admin/claimant/:oid" element={<ClaimantProfile />} />

                  {/* Operational queues — kept as their own paths but linked from Admin landing */}
                  <Route path="/payroll/queue" element={<ApprovalsPage defaultStage="payroll" />} />
                  <Route path="/payroll/awaiting-payment" element={<ApprovalsPage defaultStage="awaiting-payment" />} />
                  <Route path="/payroll/:id" element={<ApprovalDetailPage />} />
                  <Route path="/finance" element={<FinanceLanding />} />
                  <Route path="/finance/reports" element={<ReportsPage />} />
                  <Route path="/home" element={<ComingSoon title="My home" description="Petty cash float status, log spend, set activity budgets per resident." backTo="/admin" backLabel="Back to admin" />} />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AppShell>
            )}
          </RequireAuth>
        }
      />
    </Routes>
  );
}
